import type { StoreActionApi } from 'react-sweet-state';
import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';

import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { OPTIMISTIC_SAVE_COMMENT_ID } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import type { CommentLocationGenericWithId } from '@confluence/comments-panel-queries/entry-points/CommentLocation.ts';

import type { LocationValue } from './commentsPanelHelper.ts';

export enum ViewValues {
	OPEN = 'OPEN',
	UNREAD = 'UNREAD',
	DELETED_CONTENT = 'DELETED_CONTENT',
	RESOLVED = 'RESOLVED',
	MENTIONS_ME = 'MENTIONS_ME',
}

export enum SortValues {
	PAGE_ORDER = 'PAGE_ORDER',
	MOST_RECENT = 'MOST_RECENT',
}

export enum CommentMode {
	VIEW = 'view',
	ADD = 'add',
}

export enum AddFooterCommentMode {
	FOOTER = 'footer', // The footer comments section
	STICKY_DRAFT = 'sticky_draft', // The sticky draft that persists outside of the footer
	STICKY_FOOTER = 'sticky_footer', // The sticky footer that persists outside of the footer
}

export enum SidebarTabValues {
	COMMENTS = 'COMMENTS',
	SUGGESTIONS = 'SUGGESTIONS',
}

export type ReplyToCommentUser = {
	displayName?: string | null;
	accountId?: string | null;
};

// Type for cached comment summary - stores the final API response content
// This cache persists across panel open/close cycles within the same session
export type CachedSummaryState = {
	// The raw API response content (JSON string)
	content: string;
	// The pre-computed final markdown for instant display
	finalMarkdown: string;
	// The content ID to validate cache is for current page
	contentId: string;
	// The comment hash when the summary was generated
	// Used to detect stale cache when comments changed while panel was closed
	commentHash: string;
};

export type CommentsPanelState = {
	currentView: ViewValues | undefined;
	currentSort: SortValues;
	// NOTE: When there are more types, should use an array or some other data structure
	showInlineComments: boolean;
	showGeneralComments: boolean;
	currentlySelectedThreadKey: string | undefined;
	isCurrentlySelectedThreadOptimisticallyAdded: boolean;
	initialDataLoadedForViewMap: Record<ViewValues, boolean>;
	replyToCommentId: string | undefined;
	replyToCommentUser: ReplyToCommentUser | undefined;
	// Keep track of the comment IDs that are being edited in the footer
	// In the future, consider either limiting this to one at a time (same as replies) and / or sharing it with the panel
	footerEditCommentIds: Set<string>;
	// Keep track of the which threads the user is currently viewing (used for keyboard shortcuts)
	currentlyRenderedThreads: CommentLocationGenericWithId[];
	location?: LocationValue; // keep track of the location when comments gets rendered
	commentType?: CommentType;
	// Comment mode state for the footer
	commentMode: CommentMode;
	isCommentAddPromptFocused: boolean;
	commentContainerToFocus: string | undefined;
	// ADF content for footer comment draft
	addFooterCommentAdf: DocNode | undefined;
	// Cached comment summary state - persists across panel open/close cycles
	cachedSummary: CachedSummaryState | null;
	// Top-level sidebar tab (Comments vs Suggestions)
	currentSidebarTab: SidebarTabValues;
};

const getInitialState = (): CommentsPanelState => ({
	currentView: undefined,
	currentSort: SortValues.PAGE_ORDER,
	showInlineComments: true,
	showGeneralComments: true,
	currentlySelectedThreadKey: undefined,
	isCurrentlySelectedThreadOptimisticallyAdded: false,
	initialDataLoadedForViewMap: {
		[ViewValues.UNREAD]: false,
		[ViewValues.OPEN]: false,
		[ViewValues.DELETED_CONTENT]: false,
		[ViewValues.RESOLVED]: false,
		[ViewValues.MENTIONS_ME]: false,
	},
	replyToCommentId: undefined,
	replyToCommentUser: undefined,
	footerEditCommentIds: new Set(),
	currentlyRenderedThreads: [],
	location: undefined,
	commentType: undefined,
	commentMode: CommentMode.VIEW,
	isCommentAddPromptFocused: false,
	commentContainerToFocus: undefined,
	addFooterCommentAdf: undefined,
	cachedSummary: null,
	currentSidebarTab: SidebarTabValues.COMMENTS,
});

// Actions for managing the comments state in the comments panel
const actions = {
	getCommentsPanelState:
		() =>
		({ getState }: StoreActionApi<CommentsPanelState>) => {
			return getState();
		},
	setCurrentView:
		(view: ViewValues | undefined) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ currentView: view });
		},
	setCurrentSort:
		(sort: SortValues) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ currentSort: sort });
		},
	setShowInlineComments:
		(showInline?: boolean) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ showInlineComments: showInline });
		},
	setShowGeneralComments:
		(showGeneral?: boolean) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ showGeneralComments: showGeneral });
		},
	setCurrentlySelectedThreadKey:
		(threadKey: string | undefined, commentType?: CommentType, isOptimisticAddSuccess?: boolean) =>
		({ getState, setState }: StoreActionApi<CommentsPanelState>): void => {
			const { currentlySelectedThreadKey } = getState();
			if (
				!!isOptimisticAddSuccess &&
				commentType === CommentType.GENERAL &&
				currentlySelectedThreadKey !== OPTIMISTIC_SAVE_COMMENT_ID
			) {
				// This means that user has selected a different comment thread
				// after new comment was optimistically added on UI but before
				// the GQL mutation succeeded. In this case, we shouldn't set
				// currentlySelectedThreadKey to the new comment.
				return;
			}
			setState({
				currentlySelectedThreadKey: threadKey,
				commentType,
				// Note: Added for COMMENTS-6792: Optimistically adding a footer comment.
				// When adding a footer comment optimistically, setCurrentlySelectedThreadKey is
				// called twice: once when the comment is added to UI optimistically with a fake id,
				// and once more when the mutation succeeds and UI is updated with the actual id.
				// The effect of the second call is rendered on UI before the updated comment list
				// is rendered, which causes a visible flicker, where the new comment goes from
				// "selected" -> "unselected" (for an instant) -> "selected" again.
				// To fix this flicker, we keep showing the comment with fake id as "selected" when
				// this flag is true, even if value of `currentlySelectedThreadKey` has changed.
				isCurrentlySelectedThreadOptimisticallyAdded:
					commentType === CommentType.GENERAL && !!isOptimisticAddSuccess,
			});
		},
	setInitialDataLoadedForView:
		({ viewToSetLoaded }: { viewToSetLoaded: ViewValues }) =>
		({ getState, setState }: StoreActionApi<CommentsPanelState>): void => {
			const { initialDataLoadedForViewMap } = getState();

			initialDataLoadedForViewMap[viewToSetLoaded] = true;

			setState({ initialDataLoadedForViewMap });
		},
	setReplyToCommentId:
		({
			parentCommentId,
			location,
			user,
		}: { parentCommentId?: string; location?: LocationValue; user?: ReplyToCommentUser } = {}) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ replyToCommentId: parentCommentId, location, replyToCommentUser: user });
		},
	resetInitialDataLoadedMap:
		() =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState(getInitialState());
		},
	setCurrentlyRenderedThreads:
		(threads: CommentLocationGenericWithId[]) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ currentlyRenderedThreads: threads });
		},
	addFooterEditCommentId:
		(commentId: string) =>
		({ setState, getState }: StoreActionApi<CommentsPanelState>): void => {
			const { footerEditCommentIds } = getState();
			// Required to change the state object reference to trigger a re-render
			const newFooterEditCommentIds = new Set(footerEditCommentIds);
			newFooterEditCommentIds.add(commentId);
			setState({
				footerEditCommentIds: newFooterEditCommentIds,
			});
		},
	removeFooterEditCommentId:
		(commentId: string) =>
		({ setState, getState }: StoreActionApi<CommentsPanelState>): void => {
			const { footerEditCommentIds } = getState();
			// Required to change the state object reference to trigger a re-render
			const newFooterEditCommentIds = new Set(footerEditCommentIds);
			newFooterEditCommentIds.delete(commentId);
			setState({
				footerEditCommentIds: newFooterEditCommentIds,
			});
		},
	setCommentMode:
		(mode: CommentMode) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ commentMode: mode });
		},
	setIsCommentAddPromptFocused:
		(isFocused: boolean) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ isCommentAddPromptFocused: isFocused });
		},
	resetCommentMode:
		() =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ commentMode: CommentMode.VIEW, isCommentAddPromptFocused: false });
		},
	setCommentContainerToFocus:
		(commentContainerToFocus: string | undefined) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ commentContainerToFocus });
		},
	setAddFooterCommentAdf:
		(addFooterCommentAdf: DocNode | undefined) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ addFooterCommentAdf });
		},
	setCachedSummary:
		(cachedSummary: CachedSummaryState | null) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ cachedSummary });
		},
	setCurrentSidebarTab:
		(tab: SidebarTabValues) =>
		({ setState }: StoreActionApi<CommentsPanelState>): void => {
			setState({ currentSidebarTab: tab });
		},
};

const CommentsStore = createStore({
	initialState: getInitialState(),
	actions,
	name: 'CommentsStore',
});

export const useCommentsPanel = createHook(CommentsStore);
export const useCommentsPanelActions = createActionsHook(CommentsStore);
export const useCommentsPanelState = createStateHook(CommentsStore);
